import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { AlertCircleIcon, BarChart3Icon, CheckSquareIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, CreditCardIcon, FileEditIcon, FileSpreadsheetIcon, FileTextIcon, LayoutDashboardIcon, ListIcon, MenuIcon, PlusIcon, ReceiptIcon, SettingsIcon, UsersIcon, Undo2Icon, XIcon } from 'lucide-react';
import { BillingDashboard } from './BillingDashboard';
import { Customers } from './Customers';
import { ItemList } from './ItemList';
import { Invoices } from './Invoices';
import { NewInvoice } from './NewInvoice';
import { Payments } from './Payments';
import { SalesReceipts } from './SalesReceipts';
import { Quotations } from './Quotations';
import { CreditNotes } from './CreditNotes';
import { Reports } from './Reports';
import { Settings } from './Settings';
export type BillingSection = 'dashboard' | 'customers' | 'new-invoice' | 'invoices-draft' | 'invoices-sent' | 'invoices-unpaid' | 'invoices-paid' | 'invoices-partial' | 'invoices-overdue' | 'quotations' | 'receipts' | 'payments' | 'sales-receipts' | 'credit-notes' | 'item-list' | 'reports' | 'settings';
type NavigationItem = {
  id: BillingSection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
};
type NavigationGroup = {
  label: string;
  items: NavigationItem[];
};
const sectionCopy: Record<BillingSection, {
  title: string;
  subtitle: string;
}> = {
  dashboard: {
    title: 'Billing overview',
    subtitle: 'Monitor collections, receivables, and today’s billing priorities.'
  },
  customers: {
    title: 'Customers',
    subtitle: 'Find registered patients or manage walk-in billing profiles.'
  },
  'new-invoice': {
    title: 'Create invoice',
    subtitle: 'Build a bill from the service catalogue or an active patient visit.'
  },
  'invoices-draft': {
    title: 'Draft invoices',
    subtitle: 'Review invoices before sending them to customers.'
  },
  'invoices-sent': {
    title: 'Sent invoices',
    subtitle: 'Track invoices that have been issued to customers.'
  },
  'invoices-unpaid': {
    title: 'Unpaid invoices',
    subtitle: 'Follow up invoices with an outstanding balance.'
  },
  'invoices-paid': {
    title: 'Paid invoices',
    subtitle: 'Review fully settled billing records.'
  },
  'invoices-partial': {
    title: 'Partially paid invoices',
    subtitle: 'Track invoices with a remaining balance.'
  },
  'invoices-overdue': {
    title: 'Overdue invoices',
    subtitle: 'Prioritize accounts that are past their due date.'
  },
  quotations: {
    title: 'Quotations',
    subtitle: 'Prepare and manage estimates before issuing an invoice.'
  },
  receipts: {
    title: 'Receipts',
    subtitle: 'View and issue payment receipts for collected balances.'
  },
  payments: {
    title: 'Payments received',
    subtitle: 'Record payments and keep invoice balances current.'
  },
  'sales-receipts': {
    title: 'Sales receipts',
    subtitle: 'Record independent non-invoice cash sales and issue receipts.'
  },
  'credit-notes': {
    title: 'Credit notes',
    subtitle: 'Manage billing corrections, refunds, and service credits.'
  },
  'item-list': {
    title: 'Service catalogue',
    subtitle: 'Maintain the approved billable services and pricing list.'
  },
  reports: {
    title: 'Reports',
    subtitle: 'Review financial activity across billing operations.'
  },
  settings: {
    title: 'Billing settings',
    subtitle: 'Configure billing preferences and operational controls.'
  }
};
export function BillingModule() {
  const {
    currentUser,
    invoices
  } = useData();
  const [activeSection, setActiveSection] = useState<BillingSection>('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const invoiceCounts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      draft: invoices.filter(invoice => invoice.status === 'Draft').length,
      sent: invoices.filter(invoice => invoice.status === 'Sent').length,
      unpaid: invoices.filter(invoice => invoice.status === 'Unpaid').length,
      partial: invoices.filter(invoice => invoice.status === 'Partially Paid').length,
      paid: invoices.filter(invoice => invoice.status === 'Paid').length,
      overdue: invoices.filter(invoice => invoice.status === 'Overdue' || (invoice.status === 'Unpaid' || invoice.status === 'Partially Paid') && invoice.dueDate < today).length
    };
  }, [invoices]);
  if (!currentUser || currentUser.role !== 'Administrator' && currentUser.role !== 'Reception') {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-1081" data-mp="159er5erog8h7">
        You do not have permission to access the Billing Module.
      </div>;
  }
  const isAdmin = currentUser.role === 'Administrator';
  const navigationGroups: NavigationGroup[] = [{
    label: 'Overview',
    items: [{
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }]
  }, {
    label: 'Sales',
    items: [{
      id: 'customers',
      label: 'Customers',
      icon: UsersIcon
    }, {
      id: 'new-invoice',
      label: 'New Invoice',
      icon: PlusIcon
    }, {
      id: 'quotations',
      label: 'Quotations',
      icon: FileSpreadsheetIcon
    }, {
      id: 'credit-notes',
      label: 'Credit Notes',
      icon: Undo2Icon
    }]
  }, {
    label: 'Invoices',
    items: [{
      id: 'invoices-draft',
      label: 'Draft',
      icon: FileEditIcon,
      count: invoiceCounts.draft
    }, {
      id: 'invoices-sent',
      label: 'Sent',
      icon: FileTextIcon,
      count: invoiceCounts.sent
    }, {
      id: 'invoices-unpaid',
      label: 'Unpaid',
      icon: AlertCircleIcon,
      count: invoiceCounts.unpaid
    }, {
      id: 'invoices-partial',
      label: 'Partially Paid',
      icon: ClockIcon,
      count: invoiceCounts.partial
    }, {
      id: 'invoices-paid',
      label: 'Paid',
      icon: CheckSquareIcon,
      count: invoiceCounts.paid
    }, {
      id: 'invoices-overdue',
      label: 'Overdue',
      icon: AlertCircleIcon,
      count: invoiceCounts.overdue
    }]
  }, {
    label: 'Cashflow',
    items: [{
      id: 'payments',
      label: 'Payments Received',
      icon: CreditCardIcon
    }, {
      id: 'receipts',
      label: 'Invoice Receipts',
      icon: ReceiptIcon
    }, {
      id: 'sales-receipts',
      label: 'Sales Receipts',
      icon: ReceiptIcon
    }]
  }, {
    label: 'Admin',
    items: [...(isAdmin ? [{
      id: 'item-list' as BillingSection,
      label: 'Item List',
      icon: ListIcon
    }, {
      id: 'reports' as BillingSection,
      label: 'Reports',
      icon: BarChart3Icon
    }, {
      id: 'settings' as BillingSection,
      label: 'Settings',
      icon: SettingsIcon
    }] : [])]
  }];
  const selectSection = (section: BillingSection) => {
    setActiveSection(section);
    setIsMobileNavOpen(false);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <BillingDashboard onNavigate={selectSection} data-id="element-1082" data-mp="3t8irslqsd1jg" />;
      case 'customers':
        return <Customers data-id="element-1083" data-mp="20ws1476tj7v" />;
      case 'item-list':
        return <ItemList data-id="element-1084" data-mp="21pptfo64ydv" />;
      case 'new-invoice':
        return <NewInvoice onNavigate={selectSection} data-id="element-1085" data-mp="7014c3perx2f" />;
      case 'invoices-draft':
      case 'invoices-sent':
      case 'invoices-unpaid':
      case 'invoices-paid':
      case 'invoices-partial':
      case 'invoices-overdue':
        return <Invoices filter={activeSection} onNavigate={selectSection} data-id="element-1086" data-mp="gpsxqulruk5b" />;
      case 'payments':
      case 'receipts':
        return <Payments data-id="element-1087" data-mp="ku0oexbsia66" />;
      case 'sales-receipts':
        return <SalesReceipts data-id="element-1088" data-mp="ps29fnss0jka" />;
      case 'quotations':
        return <Quotations onNavigate={selectSection} data-id="element-1089" data-mp="uq3pdzuwo2rj" />;
      case 'credit-notes':
        return <CreditNotes onNavigate={selectSection} data-id="element-1090" data-mp="uo105i1kudnv" />;
      case 'reports':
        return <Reports data-id="element-1091" data-mp="2rjyimqrh1hbw" />;
      case 'settings':
        return <Settings data-id="element-1092" data-mp="2wh8dilxmql74" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Billing navigation" data-id="element-1093" data-mp="2wjkxf4my75mt">
      {navigationGroups.map(group => <div key={group.label} data-id="element-1094" data-mp="2ozsgmomf40xz">
          <p className={`chrome-label mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] ${isCollapsed && !mobile ? 'sr-only' : ''}`} data-id="element-1095" data-mp="2k6plwd5wmlg7">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-1096" data-mp="2a9umzt5pyj1e">
            {group.items.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return <button key={item.id} type="button" onClick={() => selectSection(item.id)} title={isCollapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'chrome-nav-item-active' : 'chrome-nav-item'} ${isCollapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-1097" data-mp="32ys3prua87s1">
                  <Icon className={`h-4 w-4 shrink-0 ${isCollapsed && !mobile ? '' : 'mr-3'}`} data-id="element-1098" data-mp="1chap70lag2tk" />
                  {(!isCollapsed || mobile) && <>
                      <span className="truncate" data-id="element-1099" data-mp="1m7vjwcxucn77">{item.label}</span>
                      {item.count !== undefined && item.count > 0 && <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${isActive ? 'bg-blue-100 text-blue-700' : item.id === 'invoices-overdue' ? 'bg-red-100 text-red-700' : 'bg-white text-chrome-active-ink'}`} data-id="element-1100" data-mp="1hfc9tpyd4a75">
                          {item.count}
                        </span>}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  const copy = sectionCopy[activeSection];
  const showNewInvoiceAction = activeSection !== 'new-invoice';
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-1101" data-mp="2hg7d9kogeqtx">
      <aside className={`chrome-sidebar chrome-divider hidden shrink-0 border-r lg:flex lg:flex-col ${isCollapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-1102" data-mp="2mg3g3glf0c7w">
        <div className={`chrome-divider border-b ${isCollapsed ? 'p-3' : 'p-5'}`} data-id="element-1103" data-mp="2cph92arrku0t">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`} data-id="element-1104" data-mp="1yq97jb84p9tg">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED MEDICAL CENTRE" className="h-9 w-9 rounded-lg bg-white object-contain" data-id="element-1105" data-mp="23pc92lowgxwo" />
            {!isCollapsed && <div className="min-w-0" data-id="element-1106" data-mp="3j5t5o7exu3ua">
                <p className="truncate text-sm font-bold" data-id="element-1107" data-mp="3j74c6ezrppp3">
                  BMED MEDICAL CENTRE
                </p>
                <p className="chrome-label truncate text-xs font-medium" data-id="element-1108" data-mp="3o4e0tsfc1xbv">
                  Billing &amp; Invoicing
                </p>
              </div>}
          </div>
          {!isCollapsed && <div className="mt-4 inline-flex items-center rounded-md bg-white/20 px-2.5 py-1 text-[11px] font-semibold" data-id="element-1109" data-mp="3hqqilhzwcaiz">
              Reception workspace
            </div>}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-1110" data-mp="38q24yoef0c6m">{navigation()}</div>

        <div className="chrome-divider border-t p-3" data-id="element-1111" data-mp="98c9z5v2jxhd">
          {!isCollapsed && <div className="mb-3 rounded-lg bg-white/15 px-3 py-2" data-id="element-1112" data-mp="4asn5hkb2cdt">
              <p className="truncate text-xs font-semibold" data-id="element-1113" data-mp="3vo6nuq6ghy95">
                {currentUser.fullName}
              </p>
              <p className="chrome-label text-[11px]" data-id="element-1114" data-mp="3rnmr3npsownj">Live billing access</p>
            </div>}
          <button type="button" onClick={() => setIsCollapsed(value => !value)} className="chrome-icon-button flex w-full items-center justify-center rounded-md py-2 transition-colors" aria-label={isCollapsed ? 'Expand billing navigation' : 'Collapse billing navigation'} data-id="element-1115" data-mp="s0deoouiyhyp">
            {isCollapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-1116" data-mp="2euqeurevkr6u" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-1117" data-mp="2jw7deg9w01k9" />}
          </button>
        </div>
      </aside>

      {isMobileNavOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-1118" data-mp="2os4uwpwvzdym">
          <button type="button" aria-label="Close billing navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setIsMobileNavOpen(false)} data-id="element-1119" data-mp="2tsainv01cgbf" />
          <aside className="chrome-sidebar relative flex h-full w-[280px] flex-col shadow-2xl" data-id="element-1120" data-mp="32l4ccxg8bi8t">
            <div className="chrome-divider flex items-center justify-between border-b p-5" data-id="element-1121" data-mp="37ma5uxhpr9je">
              <div className="flex items-center gap-3" data-id="element-1122" data-mp="3cklb0ux2cvva">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED MEDICAL CENTRE" className="h-9 w-9 rounded-lg bg-white object-contain" data-id="element-1123" data-mp="2ywtf5evplbvo" />
                <div data-id="element-1124" data-mp="38m4j6n3qnqcp">
                  <p className="text-sm font-bold" data-id="element-1125" data-mp="38oqumxtr2q07">BMED MEDICAL CENTRE</p>
                  <p className="chrome-label text-xs font-medium" data-id="element-1126" data-mp="33ol5ppev7hlt">
                    Billing &amp; Invoicing
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setIsMobileNavOpen(false)} className="chrome-icon-button rounded-md p-2" aria-label="Close billing navigation" data-id="element-1127" data-mp="2b074z1we676k">
                <XIcon className="h-5 w-5" data-id="element-1128" data-mp="2kzoczpz48x5p" />
              </button>
            </div>
            <div className="px-5 pt-4" data-id="element-1129" data-mp="54coh0y7wsat">
              <span className="inline-flex rounded-md bg-white/20 px-2.5 py-1 text-[11px] font-semibold" data-id="element-1130" data-mp="7co96o1ujrm">
                Reception workspace
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1131" data-mp="cpovo0e4bof5">{navigation(true)}</div>
          </aside>
        </div>}

      <section className="min-w-0 flex-1" data-id="element-1132" data-mp="3uvl5kgjhgfv2">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-1133" data-mp="3uww7sbxa5fk2">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-1134" data-mp="2tc3x824vkn1">
            <div className="flex min-w-0 items-center gap-3" data-id="element-1135" data-mp="qj6dngr5ot32">
              <button type="button" onClick={() => setIsMobileNavOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open billing navigation" data-id="element-1136" data-mp="vf50gzwu631d">
                <MenuIcon className="h-5 w-5" data-id="element-1137" data-mp="19fpk1yaks2vx" />
              </button>
              <div className="min-w-0" data-id="element-1138" data-mp="1e6o1x2b7p91a">
                <p className="mb-0.5 text-xs font-medium text-slate-500" data-id="element-1139" data-mp="1e7ha87ptk6lj">
                  Reception <span className="mx-1 text-slate-300" data-id="element-1140" data-mp="1o2xyr933eqcn">/</span>{' '}
                  Billing
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-1141" data-mp="1k2e206mflor1">
                  <h1 className="truncate text-lg font-bold text-slate-900 sm:text-xl" data-id="element-1142" data-mp="1f16tu6gdmnju">
                    {copy.title}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-1143" data-mp="1a2n7i4xvf9qy">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-1144" data-mp="155wrfhm2ge5j" />{' '}
                    Live data
                  </span>
                </div>
                <p className="hidden text-sm text-slate-500 xl:block" data-id="element-1145" data-mp="wcjodmuafw5q">
                  {copy.subtitle}
                </p>
              </div>
            </div>
            {showNewInvoiceAction && <button type="button" onClick={() => selectSection('new-invoice')} className="inline-flex shrink-0 items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-1146" data-mp="mbzj893ne7i8">
                <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1147" data-mp="h7xh9eegaukd" />
                <span className="hidden sm:inline" data-id="element-1148" data-mp="h6dx3cpobzdb">New Invoice</span>
                <span className="sm:hidden" data-id="element-1149" data-mp="m68pjkp3kx0h">Invoice</span>
              </button>}
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-1150" data-mp="3kr0d0sm18m9o">
          {renderContent()}
        </main>
      </section>
    </div>;
}